import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@environments/environment';
import { UserService } from '@services/user.service';
import { isHttpUrl } from '@shared/utils/http-url.utils';
import { BehaviorSubject, catchError, map, Observable, of, retry, take, throwError } from 'rxjs';
import { SessionStorageService } from './storage.service';

/** The presigned url a template is imported from has expired. */
export class TemplateLinkExpiredError extends Error {}

/** Needed to export templates for another environment and to import templates that come from one. The docs backend asks for the same permission when it hands out and when it accepts the presigned urls. */
export const TEMPLATE_AWS_ENV_IMPORT_EXPORT_PERMISSION = 'docs:TemplateAwsEnvImportExport';

/** What tells one template from another: its name, its description and its tags. */
export interface ITemplateIdentity {
	name: string;
	description?: string | null;
	/** The list of key and value the backend answers, the map it is given, or the text `a=1, b=2` a file and a table hold. */
	tags?: unknown;
}

/** The tags as the pairs they hold, `key=value`, in one order - so the same tags are the same whichever way round they are written, and whichever form they come in. */
function normalizeTags(tags: unknown): string[] {
	const pair = (key: unknown, value: unknown) => `${String(key).trim()}=${String(value ?? '').trim()}`;
	const fromText = (text: string) =>
		text
			.split(',')
			.map((part) => part.trim())
			.filter((part) => part.length > 0)
			.map((part) => {
				const at = part.indexOf('=');
				return at < 0 ? pair(part, '') : pair(part.slice(0, at), part.slice(at + 1));
			});

	let pairs: string[] = [];
	if (typeof tags === 'string') {
		pairs = fromText(tags);
	} else if (Array.isArray(tags)) {
		pairs = tags.flatMap((tag) => (typeof tag === 'string' ? fromText(tag) : tag && typeof tag === 'object' ? [pair((tag as { key?: unknown }).key, (tag as { value?: unknown }).value)] : []));
	} else if (tags && typeof tags === 'object') {
		pairs = Object.entries(tags).map(([key, value]) => pair(key, value));
	}

	return pairs.sort();
}

/** Two templates are the same template when this is the same: the name as written, the description without the spaces around it, and the tags in any order. */
export function templateIdentity(template: ITemplateIdentity): string {
	return JSON.stringify([template.name, (template.description ?? '').trim(), normalizeTags(template.tags)]);
}

/** How a template is named in a message: its name, and its description when it has one - the name alone does not say which of several it is. */
function templateLabel(template: ITemplateIdentity): string {
	const description = (template.description ?? '').trim();
	return description ? `${template.name} (${description})` : template.name;
}

/** The templates, named by {@link templateLabel}, that 2+ of the items are - the same name, description and tags. Each once. Blank names are skipped. */
export function findDuplicateTemplates(items: ITemplateIdentity[]): string[] {
	const seen = new Set<string>();
	const duplicates = new Set<string>();

	for (const item of items) {
		if (!item.name) {
			continue;
		}
		const identity = templateIdentity(item);
		if (seen.has(identity)) {
			duplicates.add(templateLabel(item));
		}
		seen.add(identity);
	}

	return [...duplicates];
}

/** The selected templates, named by {@link templateLabel}, that another template also is - inside the selection, or among all the templates. */
export function findTemplateClashes(selected: (ITemplateIdentity & { self: string })[], all: (ITemplateIdentity & { self: string })[]): string[] {
	const selectedSelves = new Set(selected.map((template) => template.self));
	const unselectedIdentities = new Set(all.filter((template) => !selectedSelves.has(template.self)).map((template) => templateIdentity(template)));
	const clashes = new Set(findDuplicateTemplates(selected));

	selected.filter((template) => template.name && unselectedIdentities.has(templateIdentity(template))).forEach((template) => clashes.add(templateLabel(template)));

	return [...clashes];
}

@Injectable({
	providedIn: 'root',
})
export class DocsService {
	private templateSubject$: BehaviorSubject<ITableTemplate | null>;
	public template$: Observable<ITableTemplate | null>;

	constructor(
		private httpClient: HttpClient,
		private sessionStorageService: SessionStorageService,
		private userService: UserService,
	) {
		this.templateSubject$ = new BehaviorSubject<ITableTemplate | null>(null);
		this.template$ = this.templateSubject$.asObservable();

		this.userService.accountChanged$.subscribe(() => this.templateSubject$.next(null));
	}

	private headers() {
		const accountId: string = this.userService.account.id;
		const token: string = this.sessionStorageService.get('User.Authorization');
		return new HttpHeaders({
			Authorization: `Bearer ${token}`,
			'x-account-id': accountId,
		});
	}

	deleteMappingBook(self: string, request: IDeleteMappingBookRequest): Observable<{ result: IDbMappingBook }> {
		const apiUrl = environment.awsEnvUri + self;
		return this.httpClient
			.delete<{ result: IDbMappingBook }>(apiUrl, {
				headers: this.headers(),
				body: request,
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getMappingBook(self: string): Observable<{ result: IDbMappingBook }> {
		const apiUrl = environment.awsEnvUri + self;
		return this.httpClient
			.get<{ result: IDbMappingBook[] }>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getMappingsList(): Observable<{ result: IDbMappingBook[] }> {
		const apiUrl = `${environment.awsEnvUri}/docs/mappings`;
		return this.httpClient
			.get<{ result: IDbMappingBook[] }>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	createMappingBook(mappingBook: ICreateMappingBookRequest): Observable<{ result: IDbMappingBook[] }> {
		const apiUrl = `${environment.awsEnvUri}/docs/mappings`;
		return this.httpClient
			.post(apiUrl, mappingBook, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	updateMappingBook(self: string, mappingBook: IEditMappingBookRequest, mergeMappings?: boolean): Observable<{ result: IDbMappingBook[] }> {
		const apiUrl = `${environment.awsEnvUri}${self}?mergeMappings=${mergeMappings ? 'true' : 'false'}`;
		return this.httpClient
			.put(apiUrl, mappingBook, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getTemplateList(includeSoftDeleted: boolean = false): Observable<{ result: IDbTemplateItem[] }> {
		const apiUrl = `${environment.awsEnvUri}/docs/templates${includeSoftDeleted ? '?includeSoftDeleted=true' : ''}`;

		return this.httpClient
			.get<{ result: IDbTemplateItem[] }>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getTemplateVersion(self: string = ''): Observable<{ result: IDbTemplateVersionItem }> {
		const apiUrl = environment.awsEnvUri + self;
		return this.httpClient
			.get<{ result: IDbTemplateVersionItem[] }>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	/** The presigned download url of each template version id the backend answers for (needs {@link TEMPLATE_AWS_ENV_IMPORT_EXPORT_PERMISSION}). An id it gives no url for is left out of the map - what to do about it is for the caller, which knows the template by more than its id. */
	getSignedUrls(templateVersionIds: string[]): Observable<Map<string, string>> {
		return this.httpClient.post<{ result: ITemplateSignedUrl[] }>(`${environment.awsEnvUri}/docs/templates/signed-urls`, { ids: templateVersionIds }, { headers: this.headers() }).pipe(map((response) => new Map(response.result.map((signed) => [signed.id, signed.url]))));
	}

	createTemplate(createData: any): Observable<any> {
		// The backend's XSS filter rejects the raw & of a presigned url, so a url is sent percent-encoded (and decoded there); a key is sent as is.
		const body = isHttpUrl(createData.assetKey) ? { ...createData, assetKey: encodeURIComponent(createData.assetKey) } : createData;
		if (createData.version === 1) {
			return this.httpClient
				.post(`${environment.awsEnvUri}/docs/templates`, body, {
					headers: this.headers(),
				})
				.pipe(
					// Tried once more on a failure, but not on a 410: an expired link never works the second time, and a repeated POST is a wasted call - and a second template, if the first one did arrive.
					retry({ count: 1, delay: (error) => (error?.status === 410 ? throwError(() => error) : of(undefined)) }),
					// handleError drops the status, so an expired link is told apart here.
					catchError((error) => (error?.status === 410 ? throwError(() => new TemplateLinkExpiredError()) : this.handleError(error))),
				);
		} else {
			return this.httpClient
				.put(environment.awsEnvUri + this.template.self, body, {
					headers: this.headers(),
				})
				.pipe(retry(1), catchError(this.handleError));
		}
	}

	deleteTemplate(self: string, permanent: boolean = false): Observable<Response<any>> {
		const apiUrl = environment.awsEnvUri + self + (permanent ? '?permanent=true' : '');

		return this.httpClient
			.delete<{ result: IDbMappingBook }>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	reactivateTemplate(self: string): Observable<Response<any>> {
		const apiUrl = `${environment.awsEnvUri}${self}/reactivate`;
		return this.httpClient
			.post<{ result: IDbMappingBook }>(apiUrl, null, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getUploadUrl(self: string): Observable<Response<ISignedUrlResource>> {
		const apiUrl = `${environment.awsEnvUri + self}/assets`;
		return this.httpClient
			.get<Response<ISignedUrlResource>>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	getDownloadUrl(self: string): Observable<Response<ISignedUrlResource>> {
		const apiUrl = `${environment.awsEnvUri + self}/assets/download`;
		return this.httpClient
			.get<Response<ISignedUrlResource>>(apiUrl, {
				headers: this.headers(),
			})
			.pipe(retry(1), catchError(this.handleError));
	}

	uploadAssetsFile(fileData, uploadUrl): Observable<any> {
		return this.httpClient.put(uploadUrl, fileData).pipe(take(1));
	}

	public get template(): ITableTemplate | null {
		return this.templateSubject$.value || null;
	}

	public set template(v: ITableTemplate | null) {
		this.templateSubject$.next(v);
	}

	private handleError(error: { error: { message: string }; status: any; message: any }) {
		let errorMessage = '';
		if (error.error instanceof ErrorEvent) {
			// Get client-side error
			errorMessage = error.error.message;
		} else {
			// Get server-side error
			errorMessage = `Error Code: ${error.status}\nMessage: ${error.message}`;
		}
		if (error.status !== 403) {
			return throwError(() => new Error(errorMessage));
		}
		return [];
	}
}

export interface IDeleteMappingBookRequest {
	type: string;
	section?: string | null;
	mappings?: string[] | null;
}

export interface IMappingDocument {
	[key: string]: IMappingSection;
}
export interface IMappingSection {
	[key: string]: string;
}

export interface IMappingBook {
	self: string;
	tags: string;
	createdAt: string;
	updatedAt: string | null;
	createdAtEpoch: number;
	updatedAtEpoch: number | null;
	mappingsCount: number;
	mappingDocument?: IMappingDocument | null;
}

export interface IDbMappingBook {
	self: string;
	tags: any;
	createdAt: number;
	updatedAt: number | null;
	mappingDocument?: IMappingDocument | null;
}

export interface ICreateMappingBookRequest {
	tags: any;
	mappingDocument?: IMappingDocument | null;
}

export interface IEditMappingBookRequest {
	tags: any;
	mappingDocument?: IMappingDocument | null;
}

export interface IImportedMappingBookRequest {
	tags: string;
	mappingDocument: IMappingDocument;
}
export interface ISignedUrlResource {
	url: string;
	expires: string;
	methodType: HttpVerb;
	requiredHeaders: Record<string, string>;
}

export enum HttpVerb {
	GET = 'get',
	POST = 'post',
	PUT = 'put',
	DELETE = 'delete',
}
export interface Response<T> {
	result: T;
}

export interface ITableTemplate {
	name: string;
	self: string;
	version: number;
	versionself: string;
	defaultVersion: number;
	tags: string;
	createdDate: string;
	createdAtEpoch: number;
	description: string;
	status: string;
}

export interface IDbTemplateItem extends IDbItemBase {
	softDeleted: boolean;
	managed: boolean;
	meta: {
		version: number;
		defaultVersion: number;
		createdAt: number;
	};
	description: string;
	version: ISelfObj;
	versions: ISelfObj[];
	tags: any;
}

export interface IDbTemplateVersionItem extends IDbItemBase {
	template: ISelfObj;
	meta: {
		version: number;
		createdAt: number;
	};
	input: ITemplateInput;
	output: [];
	tags: any;
}

export interface IDbItemBase {
	name: string;
	self: string;
}

export interface ISelfObj {
	self: string;
}

export interface ITemplateInput {
	validation: string;
}

export interface ITemplateCreateData {
	name: string;
	input?: ITemplateInput;
	description: string;
	outputs?: TemplateOutput[];
	version: number;
	defaultVersion: number;
	tags: Record<string, string>;
}

export interface ITemplateUploadData {
	name: string;
	description: string;
	input: ITemplateInput;
	outputs: TemplateOutput[];
	defaultVersion: number;
	tags: any;
}

export interface ITemplateSignedUrl {
	id: string;
	url: string;
}

export interface ITemplateExport {
	templateAssetKey: string;
	name: string;
	description: string;
	tags: string;
	input: ITemplateInput;
	output: TemplateOutput[];
}

type TemplateOutput = {
	name: string;
} & TemplateOutputVariants;

type TemplateOutputVariants = { type: OutputTypes.None } | { type: OutputTypes.Png } | ({ type: OutputTypes.Pdf } & IPdfOutput) | ({ type: OutputTypes.Static } & IStaticOutput) | ({ type: OutputTypes.Html } & IHtmlOutput) | ({ type: OutputTypes.Xml } & IXmlOutput);

enum OutputTypes {
	None = 0,
	Pdf = 1,
	Static = 2,
	Html = 3,
	Png = 4,
	Xml = 5,
}

export interface IHtmlOutput {
	HtmlDocument?: string;
	ContentFileName?: string;
	StylesheetFileName?: string;
	Assets: IAsset[];
}

export enum OutputType {
	pdf = 'Pdf',
	static = 'Static',
	xml = 'Xml',
}

export interface IPdfOutput {
	name: string;
	type: string;
	layout: ILayout;
	tags: any;
}

export interface IStaticOutput {
	name: string;
	type: string;
	asset: IAsset;
	tags: any;
}

export interface IXmlOutput {
	name: string;
	type: string;
	asset: IAsset;
	tags: any;
}

export interface ILayout {
	format: string;
	margins: IMargins;
	header: ITemplateRef;
	footer: ITemplateRef;
	body: ITemplateRef;
	assets: IAsset[];
	landscape: boolean;
}

export interface IMargins {
	top: number;
	bottom: number;
	left: number;
	right: number;
}

export interface ITemplateRef {
	content: string;
	style: string;
	contentFileName: string;
	stylesheetFileName: string;
}

export interface IAsset {
	path: string;
	sortIndex: number;
	type: string;
}
